export type OcrWord = {
  text: string;
  confidence?: number;
  bbox?: { x0: number; y0: number; x1: number; y1: number };
};

export type ReceiptBlock = {
  ticket_id: string;
  receipt_no: string | null;
  needs_review: boolean;
  confidence: number;
  box: { x: number; y: number; width: number; height: number };
};

const CLEAR_TICKET_CONFIDENCE = 78;

export function extractTicketId(value: unknown): string | null {
  const digits = String(value ?? "").replace(/\D/g, "");
  const match = digits.match(/566\d{6}/);
  return match ? match[0] : null;
}

export function detectReceiptBlocks(
  words: OcrWord[],
  imageWidth: number,
  imageHeight: number
): ReceiptBlock[] {
  const width = imageWidth || 1;
  const height = imageHeight || 1;
  const tokens = (words || [])
    .map((word) => ({
      text: String(word.text || ""),
      confidence: Number(word.confidence) || 0,
      x0: word.bbox?.x0 ?? 0,
      y0: word.bbox?.y0 ?? 0,
      x1: word.bbox?.x1 ?? 0,
      y1: word.bbox?.y1 ?? 0,
    }))
    .filter((token) => token.text.trim());

  const lineTol = Math.max(12, height * 0.018);
  const lines: { yc: number; words: typeof tokens }[] = [];
  const sorted = [...tokens].sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0);
  for (const token of sorted) {
    const yc = (token.y0 + token.y1) / 2;
    let line = lines.find((item) => Math.abs(item.yc - yc) < lineTol);
    if (!line) {
      line = { yc, words: [] };
      lines.push(line);
    }
    line.words.push(token);
    line.yc = (line.yc * (line.words.length - 1) + yc) / line.words.length;
  }

  const tickets: {
    ticket_id: string;
    y0: number;
    confidence: number;
  }[] = [];
  for (const line of lines) {
    const joined = line.words.map((word) => word.text).join("");
    const matches: string[] = joined.match(/566\d{6}/g) || [];
    const unique = matches.filter(
      (value, index) => matches.indexOf(value) === index
    );
    for (const ticket of unique) {
      const digitWords = line.words.filter((word) => /\d/.test(word.text));
      const confidence = digitWords.length
        ? Math.min(...digitWords.map((word) => word.confidence))
        : 0;
      tickets.push({
        ticket_id: ticket,
        y0: Math.min(...line.words.map((word) => word.y0)),
        confidence,
      });
    }
  }
  tickets.sort((a, b) => a.y0 - b.y0);

  const merged: typeof tickets = [];
  for (const ticket of tickets) {
    const prev = merged[merged.length - 1];
    if (
      prev &&
      prev.ticket_id === ticket.ticket_id &&
      Math.abs(prev.y0 - ticket.y0) < lineTol * 2
    ) {
      continue;
    }
    merged.push(ticket);
  }

  return merged.map((ticket, index) => {
    const next = merged[index + 1];
    const bottom = next ? next.y0 : height;
    const lowConfidence = ticket.confidence < CLEAR_TICKET_CONFIDENCE;
    return {
      ticket_id: ticket.ticket_id,
      receipt_no: lowConfidence ? null : ticket.ticket_id.slice(-3),
      needs_review: lowConfidence,
      confidence: ticket.confidence,
      box: {
        x: 0,
        y: Math.max(0, ticket.y0 - 8),
        width,
        height: Math.max(28, bottom - ticket.y0 - 4),
      },
    };
  });
}
